import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { createAuthAPI } from '../../utils/api';
import { toast } from 'sonner';
import { Plus, Eye, Trash2, UserCheck, UserX, TrendingUp, Edit, Search, X } from 'lucide-react';
import ClientEvolutionModal from './ClientEvolutionModal';

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEvolution, setShowEvolution] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const searchInputRef = useRef(null);
  const [editForm, setEditForm] = useState({
    nombre: '',
    apellidos: '',
    fecha_nacimiento: '',
    altura: '',
    peso_inicial: '',
    telefono: '',
    correo: '',
    direccion: '',
    password_temp: '',
    // Datos iniciales
    glucemia_inicial: '',
    colesterol_inicial: '',
    colesterol_hdl_inicial: '',
    colesterol_ldl_inicial: '',
    trigliceridos_inicial: '',
    circunferencia_cintura_inicial: '',
    circunferencia_cadera_inicial: '',
    circunferencia_pecho_inicial: '',
    circunferencia_brazo_inicial: '',
    circunferencia_muslo_inicial: '',
    porcentaje_grasa_inicial: '',
    masa_muscular_inicial: '',
    indice_visceral_inicial: ''
  });
  
  const [newClient, setNewClient] = useState({
    nombre: '',
    apellidos: '',
    fecha_nacimiento: '',
    altura: '',
    peso_inicial: '',
    telefono: '',
    direccion: '',
    correo: '',
    password_temp: '',
    // Analíticas iniciales
    glucemia_inicial: '',
    colesterol_inicial: '',
    colesterol_hdl_inicial: '',
    colesterol_ldl_inicial: '',
    trigliceridos_inicial: '',
    // Antropométricos iniciales
    circunferencia_cintura_inicial: '',
    circunferencia_cadera_inicial: '',
    circunferencia_pecho_inicial: '',
    circunferencia_brazo_inicial: '',
    circunferencia_muslo_inicial: '',
    // Composición corporal inicial
    porcentaje_grasa_inicial: '',
    masa_muscular_inicial: '',
    indice_visceral_inicial: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const api = createAuthAPI();
      const response = await api.get('/admin/clients');
      // Sort by first apellido (last name)
      const sorted = response.data.sort((a, b) => {
        const apellidoA = (a.apellidos || '').split(' ')[0].toLowerCase();
        const apellidoB = (b.apellidos || '').split(' ')[0].toLowerCase();
        return apellidoA.localeCompare(apellidoB, 'es');
      });
      setClients(sorted);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      const clientData = {
        nombre: newClient.nombre,
        apellidos: newClient.apellidos,
        fecha_nacimiento: newClient.fecha_nacimiento,
        altura: parseFloat(newClient.altura),
        peso_inicial: parseFloat(newClient.peso_inicial),
        telefono: newClient.telefono,
        direccion: newClient.direccion,
        correo: newClient.correo,
        password_temp: newClient.password_temp
      };
      
      // Add optional initial data if provided
      if (newClient.glucemia_inicial) clientData.glucemia_inicial = parseFloat(newClient.glucemia_inicial);
      if (newClient.colesterol_inicial) clientData.colesterol_inicial = parseFloat(newClient.colesterol_inicial);
      if (newClient.colesterol_hdl_inicial) clientData.colesterol_hdl_inicial = parseFloat(newClient.colesterol_hdl_inicial);
      if (newClient.colesterol_ldl_inicial) clientData.colesterol_ldl_inicial = parseFloat(newClient.colesterol_ldl_inicial);
      if (newClient.trigliceridos_inicial) clientData.trigliceridos_inicial = parseFloat(newClient.trigliceridos_inicial);
      if (newClient.circunferencia_cintura_inicial) clientData.circunferencia_cintura_inicial = parseFloat(newClient.circunferencia_cintura_inicial);
      if (newClient.circunferencia_cadera_inicial) clientData.circunferencia_cadera_inicial = parseFloat(newClient.circunferencia_cadera_inicial);
      if (newClient.circunferencia_pecho_inicial) clientData.circunferencia_pecho_inicial = parseFloat(newClient.circunferencia_pecho_inicial);
      if (newClient.circunferencia_brazo_inicial) clientData.circunferencia_brazo_inicial = parseFloat(newClient.circunferencia_brazo_inicial);
      if (newClient.circunferencia_muslo_inicial) clientData.circunferencia_muslo_inicial = parseFloat(newClient.circunferencia_muslo_inicial);
      if (newClient.porcentaje_grasa_inicial) clientData.porcentaje_grasa_inicial = parseFloat(newClient.porcentaje_grasa_inicial);
      if (newClient.masa_muscular_inicial) clientData.masa_muscular_inicial = parseFloat(newClient.masa_muscular_inicial);
      if (newClient.indice_visceral_inicial) clientData.indice_visceral_inicial = parseFloat(newClient.indice_visceral_inicial);
      
      await api.post('/admin/clients', clientData);
      toast.success('Cliente añadido exitosamente');
      setShowAddDialog(false);
      setNewClient({
        nombre: '',
        apellidos: '',
        fecha_nacimiento: '',
        altura: '',
        peso_inicial: '',
        telefono: '',
        direccion: '',
        correo: '',
        password_temp: '',
        glucemia_inicial: '',
        colesterol_inicial: '',
        colesterol_hdl_inicial: '',
        colesterol_ldl_inicial: '',
        trigliceridos_inicial: '',
        circunferencia_cintura_inicial: '',
        circunferencia_cadera_inicial: '',
        circunferencia_pecho_inicial: '',
        circunferencia_brazo_inicial: '',
        circunferencia_muslo_inicial: '',
        porcentaje_grasa_inicial: '',
        masa_muscular_inicial: '',
        indice_visceral_inicial: ''
      });
      loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir cliente');
    }
  };

  const handleToggleActive = async (clientId, currentStatus) => {
    try {
      const api = createAuthAPI();
      await api.put(`/admin/clients/${clientId}`, { activo: !currentStatus });
      toast.success(`Cliente ${!currentStatus ? 'activado' : 'desactivado'} exitosamente`);
      loadClients();
    } catch (error) {
      toast.error('Error al cambiar estado del cliente');
    }
  };

  const openDeleteDialog = (client) => {
    setClientToDelete(client);
    setShowDeleteDialog(true);
  };

  const confirmDeleteClient = async () => {
    if (!clientToDelete) return;
    
    try {
      const api = createAuthAPI();
      await api.delete(`/admin/clients/${clientToDelete.id}`);
      toast.success(`Cliente ${clientToDelete.nombre} ${clientToDelete.apellidos} eliminado definitivamente`);
      setShowDeleteDialog(false);
      setClientToDelete(null);
      loadClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar cliente');
    }
  };


  const openEditDialog = (client) => {
    setEditingClient(client);
    setEditForm({
      nombre: client.nombre,
      apellidos: client.apellidos,
      fecha_nacimiento: client.fecha_nacimiento,
      altura: client.altura,
      peso_inicial: client.peso_inicial,
      telefono: client.telefono,
      correo: client.correo || '',
      direccion: client.direccion || '',
      password_temp: '',
      glucemia_inicial: client.glucemia_inicial || '',
      colesterol_inicial: client.colesterol_inicial || '',
      colesterol_hdl_inicial: client.colesterol_hdl_inicial || '',
      colesterol_ldl_inicial: client.colesterol_ldl_inicial || '',
      trigliceridos_inicial: client.trigliceridos_inicial || '',
      circunferencia_cintura_inicial: client.circunferencia_cintura_inicial || '',
      circunferencia_cadera_inicial: client.circunferencia_cadera_inicial || '',
      circunferencia_pecho_inicial: client.circunferencia_pecho_inicial || '',
      circunferencia_brazo_inicial: client.circunferencia_brazo_inicial || '',
      circunferencia_muslo_inicial: client.circunferencia_muslo_inicial || '',
      porcentaje_grasa_inicial: client.porcentaje_grasa_inicial || '',
      masa_muscular_inicial: client.masa_muscular_inicial || '',
      indice_visceral_inicial: client.indice_visceral_inicial || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    try {
      const api = createAuthAPI();
      
      // Preparar datos - siempre enviar todos los campos obligatorios
      const updateData = {
        nombre: editForm.nombre,
        apellidos: editForm.apellidos,
        fecha_nacimiento: editForm.fecha_nacimiento,
        altura: parseFloat(editForm.altura),
        peso_inicial: parseFloat(editForm.peso_inicial),
        telefono: editForm.telefono,
        correo: editForm.correo,
        direccion: editForm.direccion || ''
      };
      
      // Agregar contraseña si fue cambiada
      if (editForm.password_temp && editForm.password_temp.trim()) {
        updateData.password_temp = editForm.password_temp;
      }
      
      // Agregar datos iniciales (convertir strings vacíos a null)
      updateData.glucemia_inicial = editForm.glucemia_inicial ? parseFloat(editForm.glucemia_inicial) : null;
      updateData.colesterol_inicial = editForm.colesterol_inicial ? parseFloat(editForm.colesterol_inicial) : null;
      updateData.colesterol_hdl_inicial = editForm.colesterol_hdl_inicial ? parseFloat(editForm.colesterol_hdl_inicial) : null;
      updateData.colesterol_ldl_inicial = editForm.colesterol_ldl_inicial ? parseFloat(editForm.colesterol_ldl_inicial) : null;
      updateData.trigliceridos_inicial = editForm.trigliceridos_inicial ? parseFloat(editForm.trigliceridos_inicial) : null;
      updateData.circunferencia_cintura_inicial = editForm.circunferencia_cintura_inicial ? parseFloat(editForm.circunferencia_cintura_inicial) : null;
      updateData.circunferencia_cadera_inicial = editForm.circunferencia_cadera_inicial ? parseFloat(editForm.circunferencia_cadera_inicial) : null;
      updateData.circunferencia_pecho_inicial = editForm.circunferencia_pecho_inicial ? parseFloat(editForm.circunferencia_pecho_inicial) : null;
      updateData.circunferencia_brazo_inicial = editForm.circunferencia_brazo_inicial ? parseFloat(editForm.circunferencia_brazo_inicial) : null;
      updateData.circunferencia_muslo_inicial = editForm.circunferencia_muslo_inicial ? parseFloat(editForm.circunferencia_muslo_inicial) : null;
      updateData.porcentaje_grasa_inicial = editForm.porcentaje_grasa_inicial ? parseFloat(editForm.porcentaje_grasa_inicial) : null;
      updateData.masa_muscular_inicial = editForm.masa_muscular_inicial ? parseFloat(editForm.masa_muscular_inicial) : null;
      updateData.indice_visceral_inicial = editForm.indice_visceral_inicial ? parseFloat(editForm.indice_visceral_inicial) : null;
      
      console.log('Enviando datos de actualización:', updateData);
      
      await api.put(`/admin/clients/${editingClient.id}`, updateData);
      toast.success('Cliente actualizado exitosamente. Los datos se han guardado en las gráficas.');
      setShowEditDialog(false);
      setEditingClient(null);
      loadClients();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar cliente');
    }
  };

  if (loading) {
    return <div>Cargando clientes...</div>;
  }

  // Filter clients by search term (first apellido)
  const filteredClients = clients.filter(client => {
    if (!searchTerm.trim()) return true;
    if (!client.apellidos) return false;
    const primerApellido = client.apellidos.split(' ')[0].toLowerCase();
    return primerApellido.includes(searchTerm.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-2xl font-bold text-green-800">Gestión de Clientes</h2>
        
        {/* Search Bar - FIX RTL ISSUE */}
        <div className="flex-1">
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar por apellido..."
                  value={searchTerm}
                  onInput={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  className="w-full pl-10 pr-10 py-3 text-base border-2 border-gray-300 focus:border-blue-500 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="search-clients-input"
                  autoComplete="off"
                  inputMode="text"
                  style={{
                    direction: 'ltr',
                    textAlign: 'left',
                    unicodeBidi: 'normal'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            {searchTerm && (
              <div className="p-2 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm text-blue-700">
                  {filteredClients.length > 0 
                    ? `✓ ${filteredClients.length} cliente(s) encontrado(s)`
                    : `No se encontraron clientes`
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="pharmacy-button text-white" data-testid="add-client-button">
              <Plus className="h-4 w-4 mr-2" />
              Añadir Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-green-800 mb-4">Nuevo Cliente</DialogTitle>
              <p className="text-sm text-gray-600">Completa todos los campos marcados con * (obligatorios)</p>
            </DialogHeader>
            <form onSubmit={handleAddClient} className="space-y-6 mt-6" data-testid="add-client-form">
              
              {/* Datos Personales */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-4 text-lg">📋 Datos Personales</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nombre" className="text-base font-semibold text-gray-700">Nombre *</Label>
                      <Input
                        id="nombre"
                        value={newClient.nombre}
                        onChange={(e) => setNewClient({...newClient, nombre: e.target.value})}
                        required
                        className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                        placeholder="Ej: María"
                        data-testid="nombre-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apellidos" className="text-base font-semibold text-gray-700">Apellidos *</Label>
                      <Input
                        id="apellidos"
                        value={newClient.apellidos}
                        onChange={(e) => setNewClient({...newClient, apellidos: e.target.value})}
                        required
                        className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                        placeholder="Ej: García López"
                        data-testid="apellidos-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fecha_nacimiento" className="text-base font-semibold text-gray-700">Fecha de Nacimiento *</Label>
                    <Input
                      id="fecha_nacimiento"
                      type="date"
                      value={newClient.fecha_nacimiento}
                      onChange={(e) => setNewClient({...newClient, fecha_nacimiento: e.target.value})}
                      required
                      className="text-base py-6 border-2 border-gray-300 focus:border-green-500"
                      data-testid="fecha-nacimiento-input"
                    />
                  </div>
                </div>
              </div>

              {/* Datos Físicos */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-4 text-lg">📏 Datos Físicos</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="altura" className="text-base font-semibold text-gray-700">Altura (cm) *</Label>
                    <Input
                      id="altura"
                      type="number"
                      step="0.1"
                      value={newClient.altura}
                      onChange={(e) => setNewClient({...newClient, altura: e.target.value})}
                      required
                      className="text-base py-6 border-2 border-gray-300 focus:border-blue-500"
                      placeholder="Ej: 165"
                      data-testid="altura-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="peso" className="text-base font-semibold text-gray-700">Peso Inicial (kg) *</Label>
                    <Input
                      id="peso"
                      type="number"
                      step="0.1"
                      value={newClient.peso_inicial}
                      onChange={(e) => setNewClient({...newClient, peso_inicial: e.target.value})}
                      required
                      className="text-base py-6 border-2 border-gray-300 focus:border-blue-500"
                      placeholder="Ej: 70"
                      data-testid="peso-input"
                    />
                  </div>
                </div>
              </div>

              {/* Datos de Contacto */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-800 mb-4 text-lg">📞 Datos de Contacto</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="correo" className="text-base font-semibold text-gray-700">Correo Electrónico *</Label>
                    <Input
                      id="correo"
                      type="email"
                      value={newClient.correo}
                      onChange={(e) => setNewClient({...newClient, correo: e.target.value})}
                      required
                      className="text-base py-6 border-2 border-gray-300 focus:border-purple-500"
                      placeholder="cliente@ejemplo.com"
                      data-testid="correo-input"
                    />
                    <p className="text-sm text-purple-700">
                      ℹ️ El cliente usará este correo para iniciar sesión
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefono" className="text-base font-semibold text-gray-700">Teléfono *</Label>
                    <Input
                      id="telefono"
                      type="tel"
                      value={newClient.telefono}
                      onChange={(e) => setNewClient({...newClient, telefono: e.target.value})}
                      required
                      className="text-base py-6 border-2 border-gray-300 focus:border-purple-500"
                      placeholder="666 123 456"
                      data-testid="telefono-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="direccion" className="text-base font-semibold text-gray-700">Dirección</Label>
                    <Input
                      id="direccion"
                      value={newClient.direccion}
                      onChange={(e) => setNewClient({...newClient, direccion: e.target.value})}
                      className="text-base py-6 border-2 border-gray-300 focus:border-purple-500"
                      placeholder="Calle Principal 123"
                      data-testid="direccion-input"
                    />
                  </div>
                </div>
              </div>

              {/* Contraseña Temporal */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-semibold text-amber-800 mb-4 text-lg">🔐 Contraseña Temporal</h3>
                <div className="space-y-2">
                  <Label htmlFor="password_temp" className="text-base font-semibold text-gray-700">Contraseña *</Label>
                  <Input
                    id="password_temp"
                    type="text"
                    value={newClient.password_temp}
                    onChange={(e) => setNewClient({...newClient, password_temp: e.target.value})}
                    required
                    className="text-base py-6 border-2 border-gray-300 focus:border-amber-500 font-mono"
                    placeholder="contraseña123"
                    data-testid="password-temp-input"
                  />
                  <p className="text-sm text-amber-700">
                    💡 Esta contraseña será enviada al cliente para que pueda acceder a su cuenta
                  </p>
                </div>
              </div>

              {/* Analíticas Iniciales (Opcional) */}
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-semibold text-red-800 mb-4 text-lg">🩸 Analíticas Iniciales (Opcional)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Glucemia (mg/dL)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.glucemia_inicial}
                      onChange={(e) => setNewClient({...newClient, glucemia_inicial: e.target.value})}
                      placeholder="85"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Colesterol Total</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.colesterol_inicial}
                      onChange={(e) => setNewClient({...newClient, colesterol_inicial: e.target.value})}
                      placeholder="200"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Colesterol HDL</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.colesterol_hdl_inicial}
                      onChange={(e) => setNewClient({...newClient, colesterol_hdl_inicial: e.target.value})}
                      placeholder="50"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Colesterol LDL</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.colesterol_ldl_inicial}
                      onChange={(e) => setNewClient({...newClient, colesterol_ldl_inicial: e.target.value})}
                      placeholder="100"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Triglicéridos</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.trigliceridos_inicial}
                      onChange={(e) => setNewClient({...newClient, trigliceridos_inicial: e.target.value})}
                      placeholder="150"
                      className="text-base py-5 border-2"
                    />
                  </div>
                </div>
              </div>

              {/* Medidas Antropométricas Iniciales (Opcional) */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="font-semibold text-yellow-800 mb-4 text-lg">📐 Medidas Iniciales (Opcional)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Cintura (cm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.circunferencia_cintura_inicial}
                      onChange={(e) => setNewClient({...newClient, circunferencia_cintura_inicial: e.target.value})}
                      placeholder="85"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Cadera (cm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.circunferencia_cadera_inicial}
                      onChange={(e) => setNewClient({...newClient, circunferencia_cadera_inicial: e.target.value})}
                      placeholder="95"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Pecho (cm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.circunferencia_pecho_inicial}
                      onChange={(e) => setNewClient({...newClient, circunferencia_pecho_inicial: e.target.value})}
                      placeholder="90"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Brazo (cm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.circunferencia_brazo_inicial}
                      onChange={(e) => setNewClient({...newClient, circunferencia_brazo_inicial: e.target.value})}
                      placeholder="30"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Muslo (cm)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.circunferencia_muslo_inicial}
                      onChange={(e) => setNewClient({...newClient, circunferencia_muslo_inicial: e.target.value})}
                      placeholder="55"
                      className="text-base py-5 border-2"
                    />
                  </div>
                </div>
              </div>

              {/* Composición Corporal Inicial (Opcional) */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-semibold text-indigo-800 mb-4 text-lg">⚖️ Composición Corporal Inicial (Opcional)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">% Grasa</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.porcentaje_grasa_inicial}
                      onChange={(e) => setNewClient({...newClient, porcentaje_grasa_inicial: e.target.value})}
                      placeholder="25"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Masa Muscular (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.masa_muscular_inicial}
                      onChange={(e) => setNewClient({...newClient, masa_muscular_inicial: e.target.value})}
                      placeholder="45"
                      className="text-base py-5 border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Índice Visceral</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newClient.indice_visceral_inicial}
                      onChange={(e) => setNewClient({...newClient, indice_visceral_inicial: e.target.value})}
                      placeholder="8"
                      className="text-base py-5 border-2"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full pharmacy-button text-white py-6 text-lg font-semibold" data-testid="submit-add-client-button">
                ✓ Añadir Cliente
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Clients Grid */}
      <div className="grid gap-4">
        {filteredClients.map((client) => (
          <Card key={client.id} className="pharmacy-card">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-green-800">
                      {client.nombre} {client.apellidos}
                    </h3>
                    {client.activo ? (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-semibold whitespace-nowrap">✓ Activo</span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-semibold whitespace-nowrap">✕ Inactivo</span>
                    )}
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">📧</span> 
                      <span className="truncate">{client.correo}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">📱</span> 
                      <span>{client.telefono}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">📏</span> {client.altura} cm | 
                      <span className="font-semibold">⚖️</span> {client.peso_inicial} kg
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => {
                      setSelectedClient(client);
                      setShowEvolution(true);
                    }}
                    variant="outline"
                    size="lg"
                    className="flex items-center gap-2 min-w-[180px] bg-green-50 border-2 border-green-600 text-green-800 hover:bg-green-100 shadow-lg shadow-green-500/50 font-semibold"
                    data-testid={`view-evolution-${client.id}`}
                  >
                    <TrendingUp className="h-5 w-5" />
                    Ver Evolución
                  </Button>
                  <Button
                    onClick={() => openEditDialog(client)}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 min-w-[160px]"
                    data-testid={`edit-client-${client.id}`}
                  >
                    <Edit className="h-4 w-4" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleToggleActive(client.id, client.activo)}
                    variant={client.activo ? "outline" : "default"}
                    size="sm"
                    className="flex items-center gap-2 min-w-[160px]"
                  >
                    {client.activo ? (
                      <><UserX className="h-4 w-4" /> Desactivar</>
                    ) : (
                      <><UserCheck className="h-4 w-4" /> Activar</>
                    )}
                  </Button>
                  <Button
                    onClick={() => openDeleteDialog(client)}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 min-w-[160px] border-gray-300 text-gray-700 hover:bg-gray-100"
                    data-testid={`delete-client-${client.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                    Eliminar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedClient && (
        <ClientEvolutionModal
          client={selectedClient}
          open={showEvolution}
          onClose={() => {
            setShowEvolution(false);
            setSelectedClient(null);
          }}
        />
      )}

      {/* Edit Client Dialog - FIXED SCROLLING AND ADDED ALL FIELDS */}
      {showEditDialog && editingClient && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-white flex flex-col">
            <DialogHeader className="flex-shrink-0 pb-4 border-b">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold text-green-800">
                  Editar Cliente: {editingClient.nombre} {editingClient.apellidos}
                </DialogTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowEditDialog(false)}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-2">Modifica los campos que necesites actualizar</p>
            </DialogHeader>
            
            {/* SCROLLABLE FORM CONTENT */}
            <div className="flex-1 overflow-y-auto px-1 py-4">
              <form onSubmit={handleUpdateClient} className="space-y-6" data-testid="edit-client-form">
                
                {/* Datos Personales */}
                <div className="bg-green-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-green-800 text-lg">📋 Datos Personales</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-nombre" className="text-base font-semibold">Nombre *</Label>
                      <Input
                        id="edit-nombre"
                        value={editForm.nombre}
                        onChange={(e) => setEditForm({...editForm, nombre: e.target.value})}
                        required
                        className="text-base py-6 border-2"
                        data-testid="edit-nombre-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-apellidos" className="text-base font-semibold">Apellidos *</Label>
                      <Input
                        id="edit-apellidos"
                        value={editForm.apellidos}
                        onChange={(e) => setEditForm({...editForm, apellidos: e.target.value})}
                        required
                        className="text-base py-6 border-2"
                        data-testid="edit-apellidos-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-fecha" className="text-base font-semibold">Fecha de Nacimiento *</Label>
                    <Input
                      id="edit-fecha"
                      type="date"
                      value={editForm.fecha_nacimiento}
                      onChange={(e) => setEditForm({...editForm, fecha_nacimiento: e.target.value})}
                      required
                      className="text-base py-6 border-2"
                      data-testid="edit-fecha-input"
                    />
                  </div>
                </div>

                {/* Datos Físicos */}
                <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-blue-800 text-lg">📏 Datos Físicos</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-altura" className="text-base font-semibold">Altura (cm) *</Label>
                      <Input
                        id="edit-altura"
                        type="number"
                        step="0.1"
                        value={editForm.altura}
                        onChange={(e) => setEditForm({...editForm, altura: e.target.value})}
                        required
                        className="text-base py-6 border-2"
                        data-testid="edit-altura-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-peso" className="text-base font-semibold">Peso Inicial (kg) *</Label>
                      <Input
                        id="edit-peso"
                        type="number"
                        step="0.1"
                        value={editForm.peso_inicial}
                        onChange={(e) => setEditForm({...editForm, peso_inicial: e.target.value})}
                        required
                        className="text-base py-6 border-2"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-purple-800 text-lg">📞 Datos de Contacto</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-correo" className="text-base font-semibold">Correo Electrónico *</Label>
                      <Input
                        id="edit-correo"
                        type="email"
                        value={editForm.correo}
                        onChange={(e) => setEditForm({...editForm, correo: e.target.value})}
                        required
                        className="text-base py-6 border-2"
                        data-testid="edit-correo-input"
                      />
                      <p className="text-sm text-purple-700">
                        ℹ️ El cliente usará este correo para iniciar sesión
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-telefono" className="text-base font-semibold">Teléfono *</Label>
                      <Input
                        id="edit-telefono"
                        type="tel"
                        value={editForm.telefono}
                        onChange={(e) => setEditForm({...editForm, telefono: e.target.value})}
                        required
                        className="text-base py-6 border-2"
                        data-testid="edit-telefono-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-direccion" className="text-base font-semibold">Dirección</Label>
                      <Input
                        id="edit-direccion"
                        value={editForm.direccion}
                        onChange={(e) => setEditForm({...editForm, direccion: e.target.value})}
                        className="text-base py-6 border-2"
                        data-testid="edit-direccion-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-amber-800 text-lg">🔐 Cambiar Contraseña</h3>
                  <div className="space-y-2">
                    <Label htmlFor="edit-password" className="text-base font-semibold">Nueva Contraseña (dejar vacío para no cambiar)</Label>
                    <Input
                      id="edit-password"
                      type="text"
                      value={editForm.password_temp}
                      onChange={(e) => setEditForm({...editForm, password_temp: e.target.value})}
                      className="text-base py-6 border-2 font-mono"
                      placeholder="Nueva contraseña"
                      data-testid="edit-password-input"
                    />
                  </div>
                </div>

                {/* Datos Iniciales Editables - EXPANDED */}
                <div className="bg-red-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-red-800 mb-4 text-lg">🩸 Analíticas Iniciales (Editables)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Glucemia (mg/dL)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.glucemia_inicial} 
                        onChange={(e) => setEditForm({...editForm, glucemia_inicial: e.target.value})} 
                        placeholder="85" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Colesterol Total</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.colesterol_inicial} 
                        onChange={(e) => setEditForm({...editForm, colesterol_inicial: e.target.value})} 
                        placeholder="200" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Colesterol HDL</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.colesterol_hdl_inicial} 
                        onChange={(e) => setEditForm({...editForm, colesterol_hdl_inicial: e.target.value})} 
                        placeholder="50" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Colesterol LDL</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.colesterol_ldl_inicial} 
                        onChange={(e) => setEditForm({...editForm, colesterol_ldl_inicial: e.target.value})} 
                        placeholder="100" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Triglicéridos</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.trigliceridos_inicial} 
                        onChange={(e) => setEditForm({...editForm, trigliceridos_inicial: e.target.value})} 
                        placeholder="150" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-yellow-800 mb-4 text-lg">📐 Medidas Iniciales (Editables)</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Cintura (cm)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.circunferencia_cintura_inicial} 
                        onChange={(e) => setEditForm({...editForm, circunferencia_cintura_inicial: e.target.value})} 
                        placeholder="85" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Cadera (cm)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.circunferencia_cadera_inicial} 
                        onChange={(e) => setEditForm({...editForm, circunferencia_cadera_inicial: e.target.value})} 
                        placeholder="95" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Pecho (cm)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.circunferencia_pecho_inicial} 
                        onChange={(e) => setEditForm({...editForm, circunferencia_pecho_inicial: e.target.value})} 
                        placeholder="90" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Brazo (cm)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.circunferencia_brazo_inicial} 
                        onChange={(e) => setEditForm({...editForm, circunferencia_brazo_inicial: e.target.value})} 
                        placeholder="30" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Muslo (cm)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.circunferencia_muslo_inicial} 
                        onChange={(e) => setEditForm({...editForm, circunferencia_muslo_inicial: e.target.value})} 
                        placeholder="55" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-semibold text-indigo-800 mb-4 text-lg">⚖️ Composición Corporal Inicial (Editables)</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">% Grasa</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.porcentaje_grasa_inicial} 
                        onChange={(e) => setEditForm({...editForm, porcentaje_grasa_inicial: e.target.value})} 
                        placeholder="25" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Masa Muscular (kg)</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.masa_muscular_inicial} 
                        onChange={(e) => setEditForm({...editForm, masa_muscular_inicial: e.target.value})} 
                        placeholder="45" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Índice Visceral</Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={editForm.indice_visceral_inicial} 
                        onChange={(e) => setEditForm({...editForm, indice_visceral_inicial: e.target.value})} 
                        placeholder="8" 
                        className="text-base py-5 border-2" 
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 sticky bottom-0 bg-white pt-4 pb-2 border-t">
                  <Button type="submit" className="flex-1 pharmacy-button text-white py-6" data-testid="submit-edit-button">
                    💾 Guardar Cambios
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="px-8">
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && clientToDelete && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
                <Trash2 className="h-6 w-6" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-gray-800 font-semibold mb-2">
                  ⚠️ Esta acción es PERMANENTE y NO se puede deshacer
                </p>
                <p className="text-sm text-gray-700">
                  Estás a punto de eliminar definitivamente a:
                </p>
                <p className="text-base font-bold text-gray-900 mt-2">
                  {clientToDelete.nombre} {clientToDelete.apellidos}
                </p>
              </div>
              <p className="text-sm text-gray-600">
                Se eliminarán todos los datos del cliente incluyendo:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-2">
                <li>Información personal</li>
                <li>Historial de peso y mediciones</li>
                <li>Productos recomendados y comprados</li>
                <li>Todos los registros asociados</li>
              </ul>
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setClientToDelete(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmDeleteClient}
                  variant="destructive"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Sí, Eliminar Definitivamente
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ClientManagement;